// « Non facturés » — stays over whose Vega bill was posted and never invoiced
// (PMS-TERMINAL-LINK-BRIEF §8.8i, operator 2026-09-25).
//
// Found by chance on 2026-09-25: TRAVEL COUNSELLORS, nine rooms, left on 13/09 —
// every night posted by the auto facturation, 3 732,96 of arrhes received by
// transfer, and no invoice at all. Nothing in Vega points at such a stay: its
// pending-notes screen lists notes, and this one never had one.
//
// The list: guests gone (Vega status P, or the departure date passed), live
// lines not yet invoiced, no pending note. Each row is an invoice to make in
// Vega — usually against the arrhes already held — so that it reaches the
// month's accounting files. Deleted lines never appear (the record state,
// 20260925080100). Interim source: the Vega mirror.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { DepartureBriefing, DismissedUnissued, UnissuedBill } from '@lemontaigu/pms-core';
import { fmtAmount, fmtDate } from './logic.js';
import { SheetTop } from './SheetTop.js';
import { clientTag, dayMonth, displayRoom, vegaReadLabel } from './departureSheet.js';
import { PaymentList } from './PaymentList.js';
import { EtablissementTag } from './EtablissementTag.js';

export interface UnissuedProps {
  briefing: DepartureBriefing;
  /** The view chips, placed at the top by the Encaissement screen (§8.8k). */
  nav?: ReactNode;
  /** The Encaissement form, placed under the sheet's title. */
  form?: ReactNode;
  /** Injected for tests; the wall clock otherwise. */
  now?: Date;
}

const STATUS_LABEL: Record<string, string> = {
  departed: 'parti',
  in_house: 'encore là dans Vega (départ passé)',
  confirmed: 'jamais arrivé dans Vega',
  reserved: 'jamais arrivé dans Vega',
};

export function Unissued({ briefing, nav, form, now }: UnissuedProps) {
  const [rows, setRows] = useState<UnissuedBill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<DismissedUnissued[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await briefing.unissued();
    setLoading(false);
    if (!r.ok) {
      setError(r.error.message ?? 'lecture impossible');
      setRows([]);
      return;
    }
    setRows(r.value);
    // The set-aside list is a convenience: a failure here must not hide the list.
    const d = await briefing.dismissedUnissued();
    setDismissed(d.ok ? d.value : []);
  }, [briefing]);

  // « Écarter » — for a leftover Vega's own screens can no longer reach (a
  // breakfast posted after a settled invoice, on a closed month). The reason is
  // mandatory and the server refuses a short one.
  const setAside = async (b: UnissuedBill) => {
    const typed = window.prompt(
      `Écarter ${b.partyLabel} (${fmtAmount(b.amountCents)}) de « Non facturés » ?

Motif (obligatoire, 10 caractères minimum) :`,
      '',
    );
    if (typed === null) return;
    const reason = typed.trim();
    if (reason.length < 10) {
      setError('Motif trop court : dites pourquoi cette ligne n’a pas à être facturée.');
      return;
    }
    const r = await briefing.dismissUnissued(b.propertyCode, b.bookingRef, reason);
    if (!r.ok) {
      setError(r.error.message ?? 'écart impossible');
      return;
    }
    await load();
  };

  const putBack = async (d: DismissedUnissued) => {
    const r = await briefing.restoreUnissued(d.propertyCode, d.bookingRef);
    if (!r.ok) {
      setError(r.error.message ?? 'rétablissement impossible');
      return;
    }
    await load();
  };

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(
    () => rows.reduce((t, b) => ({ amount: t.amount + b.amountCents, deposit: t.deposit + b.depositCents }), { amount: 0, deposit: 0 }),
    [rows],
  );
  const readAt = rows.find((b) => b.readAt)?.readAt ?? null;
  const read = vegaReadLabel(readAt, now ?? new Date());
  const plural = (n: number) => (n > 1 ? 's' : '');

  return (
    <section className="pms-print" aria-label="Non facturés">
      <SheetTop nav={nav} onRefresh={() => void load()} canPrint={rows.length > 0} />
      <div className="pms-head">
        <div>
          <h2>Séjours terminés non facturés</h2>
          <p className="pms-hint">
            {read && (
              <span className={read.stale ? 'pms-stale' : undefined}>
                {read.stale ? '⚠ ' : ''}
                {read.text}
                {' · '}
              </span>
            )}
            Clients partis dont la note Vega a des lignes postées mais ni facture ni note en attente : la facture est à
            faire dans Vega, le plus souvent contre les arrhes déjà reçues, pour qu’elle entre dans la compta du mois.
          </p>
        </div>
      </div>

      {form && <div className="pms-no-print pms-sheet-form">{form}</div>}
      {error && <p className="pms-warn">⚠ {error}</p>}
      {loading && <p className="pms-hint pms-no-print">Chargement…</p>}

      <div className="pms-table-wrap">
        <table className="pms-table" aria-label="Séjours non facturés">
          <thead>
            <tr>
              <th>Départ</th>
              <th>Chambre(s)</th>
              <th>Client</th>
              <th>Lignes non facturées</th>
              <th>Montant</th>
              <th>Arrhes détenues</th>
              <th>Vega</th>
              <th className="pms-no-print" />
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.bookingRef}>
                <td>{b.departureDate ? fmtDate(b.departureDate) : '—'}</td>
                <td>{b.rooms.map(displayRoom).join(', ') || '—'}</td>
                <td>
                  {b.partyLabel}
                  <span className="pms-sub"> {clientTag(b.clientRef, b.bookingRef)}</span>
                  <EtablissementTag code={b.propertyCode} />
                  <PaymentList payments={b.payments} />
                </td>
                <td>
                  {b.lineCount} ligne{plural(b.lineCount)}
                  <span className="pms-sub">
                    {' '}
                    · {dayMonth(b.firstDay)}
                    {b.lastDay && b.lastDay !== b.firstDay ? ` → ${dayMonth(b.lastDay)}` : ''}
                  </span>
                </td>
                <td className="pms-num">
                  <strong>{fmtAmount(b.amountCents)}</strong>
                </td>
                <td className="pms-num">
                  {b.depositCents ? fmtAmount(b.depositCents) : '—'}
                  {b.depositUnattributedCents !== 0 && (
                    <span
                      className="pms-sub"
                      title="Arrhes d'un client ayant plusieurs réservations : à vérifier dans Vega"
                    >
                      {' '}
                      + {fmtAmount(b.depositUnattributedCents)} à vérifier
                    </span>
                  )}
                </td>
                <td className="pms-sub">{STATUS_LABEL[b.vegaStatus ?? ''] ?? b.vegaStatus ?? '—'}</td>
                <td className="pms-no-print">
                  <button
                    type="button"
                    className="pms-mini"
                    title="Cette ligne n’a pas à être facturée et Vega ne permet plus de la retirer"
                    onClick={() => void setAside(b)}
                  >
                    Écarter
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && !error && (
              <tr>
                <td colSpan={8} className="pms-empty">
                  Aucun séjour terminé sans facture.
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={4} className="pms-sub">
                  {rows.length} séjour{plural(rows.length)} à facturer
                </td>
                <td className="pms-num">{fmtAmount(totals.amount)}</td>
                <td className="pms-num">{fmtAmount(totals.deposit)}</td>
                <td />
                <td className="pms-no-print" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {dismissed.length > 0 && (
        <details className="pms-no-print pms-dismissed">
          <summary>Écartés ({dismissed.length})</summary>
          <p className="pms-hint">
            Lignes que Vega affiche encore comme non facturées mais qu’on a décidé de ne pas facturer. Rien n’est
            supprimé : « Rétablir » les remet dans la liste.
          </p>
          <table className="pms-table" aria-label="Séjours écartés">
            <thead>
              <tr>
                <th>Client</th>
                <th>Lignes</th>
                <th>Montant</th>
                <th>Motif</th>
                <th>Écarté le</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dismissed.map((d) => (
                <tr key={`${d.propertyCode}/${d.bookingRef}`}>
                  <td>
                    {d.partyLabel}
                    <span className="pms-sub"> {d.bookingRef}</span>
                    <EtablissementTag code={d.propertyCode} />
                  </td>
                  <td>
                    {d.lineCount} ligne{plural(d.lineCount)}
                    {d.firstDay && <span className="pms-sub"> · {dayMonth(d.firstDay)}</span>}
                  </td>
                  <td className="pms-num">{fmtAmount(d.amountCents)}</td>
                  <td>{d.reason}</td>
                  <td className="pms-sub">{fmtDate(d.dismissedAt.slice(0, 10))}</td>
                  <td>
                    <button type="button" className="pms-mini" onClick={() => void putBack(d)}>
                      Rétablir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
}
